import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Input,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { CheckCircleOutlined, CopyOutlined, CloudSyncOutlined } from '@ant-design/icons';
import {
  buildSkillDeployDriftCommand,
  buildVerifySkillManifestsCommand,
  parseSkillVerifyReceipt,
  type SkillVerifyReceiptSummary,
  type SkillVerifySkillRow,
} from '../../../utils/skillManifestVerifyCli';

const { Text, Paragraph, Title } = Typography;
const { TextArea } = Input;

interface AgentSkillVerifyPanelProps {
  skillBundles: string[];
}

async function copyText(text: string, label: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    message.success(`Copied ${label}`);
  } catch {
    message.error(`Could not copy ${label}`);
  }
}

export const AgentSkillVerifyPanel: React.FC<AgentSkillVerifyPanelProps> = ({ skillBundles }) => {
  const verifyCmd = useMemo(() => buildVerifySkillManifestsCommand(skillBundles), [skillBundles]);
  const driftCmd = useMemo(() => buildSkillDeployDriftCommand(skillBundles), [skillBundles]);
  const [paste, setPaste] = useState('');
  const [summary, setSummary] = useState<SkillVerifyReceiptSummary | null>(null);

  const handleParse = () => {
    const next = parseSkillVerifyReceipt(paste);
    setSummary(next);
    if (next.parseError) {
      message.warning(next.parseError);
    } else {
      message.success(
        next.kind === 'deploy_drift'
          ? 'Deploy-drift receipt loaded'
          : next.kind === 'manifest'
            ? 'Manifest verify receipt loaded'
            : 'Receipt loaded'
      );
    }
  };

  const columns = [
    {
      title: 'Skill',
      dataIndex: 'skill',
      key: 'skill',
      render: (v: string) => <Text code>{v}</Text>,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: unknown, row: SkillVerifySkillRow) => {
        if (row.deployDrift === true) return <Tag color="orange">deploy drift</Tag>;
        if (row.deployDrift === false) return <Tag color="green">tracked</Tag>;
        if (row.status) return <Tag color={row.status === 'ok' ? 'green' : 'red'}>{row.status}</Tag>;
        return <Tag>—</Tag>;
      },
    },
    {
      title: 'Registry',
      key: 'registry',
      render: (_: unknown, row: SkillVerifySkillRow) => {
        if (!row.deployState && !row.currentVersion) return <Text type="secondary">—</Text>;
        return (
          <span>
            {row.deployState ? <Tag>{row.deployState}</Tag> : null}
            {row.currentVersion ? <Text type="secondary">{row.currentVersion}</Text> : null}
          </span>
        );
      },
    },
    {
      title: 'Notes',
      key: 'notes',
      render: (_: unknown, row: SkillVerifySkillRow) => {
        const bits: string[] = [];
        if (row.driftReasons?.length) bits.push(row.driftReasons.join(', '));
        if (row.onlyInManifest?.length) bits.push(`manifest-only: ${row.onlyInManifest.length}`);
        if (row.onlyOnDisk?.length) bits.push(`disk-only: ${row.onlyOnDisk.length}`);
        if (row.staleFiles?.length) bits.push(`stale: ${row.staleFiles.length}`);
        return bits.length ? <Text type="secondary">{bits.join(' · ')}</Text> : <Text type="secondary">—</Text>;
      },
    },
  ];

  return (
    <div
      className="agent-skill-verify-panel"
      data-testid="agent-skill-verify-panel"
      style={{ marginTop: 24 }}
    >
      <Title level={5} style={{ marginTop: 0 }}>
        <CloudSyncOutlined /> Manifest / deploy verify
      </Title>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Operator CLI handoff (no Backend proxy yet)"
        description={
          <span>
            Run these against your Kryspace workspace (<Text code>KRYSPACE_WS</Text>), then paste the
            JSON receipt here. Admin does not write <Text code>deploy_state</Text>. MCP equivalents:{' '}
            <Text code>kryspace_verify_skill_manifests</Text>,{' '}
            <Text code>kryspace_skill_deploy_drift</Text>.
          </span>
        }
      />

      {skillBundles.length === 0 ? (
        <Alert
          type="warning"
          showIcon
          message="Bind a skill first"
          description="Resolved skill bundles drive the --skill filters below."
        />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong>Check manifests</Text>
            <Paragraph
              copyable={{ text: verifyCmd }}
              style={{
                marginBottom: 8,
                marginTop: 4,
                fontFamily: 'monospace',
                fontSize: 12,
                background: 'var(--ant-color-fill-quaternary, #f5f5f5)',
                padding: 8,
                borderRadius: 4,
              }}
            >
              {verifyCmd}
            </Paragraph>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => void copyText(verifyCmd, 'manifest command')}
            >
              Copy command
            </Button>
          </div>

          <div>
            <Text strong>Deploy drift</Text>
            <Paragraph
              copyable={{ text: driftCmd }}
              style={{
                marginBottom: 8,
                marginTop: 4,
                fontFamily: 'monospace',
                fontSize: 12,
                background: 'var(--ant-color-fill-quaternary, #f5f5f5)',
                padding: 8,
                borderRadius: 4,
              }}
            >
              {driftCmd}
            </Paragraph>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => void copyText(driftCmd, 'deploy-drift command')}
            >
              Copy command
            </Button>
          </div>

          <div>
            <Text strong>Paste CLI JSON receipt</Text>
            <TextArea
              value={paste}
              onChange={(e) => setPaste(e.target.value)}
              rows={6}
              placeholder='{ "ok": true, "skills": [ ... ], "receipt": { ... } }'
              style={{ marginTop: 8, fontFamily: 'monospace', fontSize: 12 }}
              data-testid="skill-verify-receipt-paste"
            />
            <Space style={{ marginTop: 8 }}>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={handleParse}
                disabled={!paste.trim()}
              >
                Surface receipt
              </Button>
              <Button
                onClick={() => {
                  setPaste('');
                  setSummary(null);
                }}
              >
                Clear
              </Button>
            </Space>
          </div>
        </Space>
      )}

      {summary && !summary.parseError && (
        <div style={{ marginTop: 16 }}>
          <Space wrap style={{ marginBottom: 8 }}>
            <Tag color={summary.kind === 'deploy_drift' ? 'purple' : 'blue'}>
              {summary.kind === 'deploy_drift'
                ? 'deploy drift'
                : summary.kind === 'manifest'
                  ? 'manifest verify'
                  : 'receipt'}
            </Tag>
            {summary.ok === true && <Tag color="green">ok</Tag>}
            {summary.ok === false && <Tag color="red">not ok</Tag>}
            {typeof summary.skillsScanned === 'number' && (
              <Text type="secondary">scanned {summary.skillsScanned}</Text>
            )}
            {typeof summary.skillsWithDrift === 'number' && (
              <Text type="secondary">drift {summary.skillsWithDrift}</Text>
            )}
            {summary.verificationRunAt && (
              <Text type="secondary">{summary.verificationRunAt}</Text>
            )}
          </Space>
          <Table
            size="small"
            rowKey="skill"
            pagination={false}
            columns={columns}
            dataSource={summary.skills}
          />
        </div>
      )}
    </div>
  );
};
